import { Suspense } from 'react';
import { Box, Grid, Page, PageBody, Spinner, Stack } from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { channelValueOf } from '@/features/deliveries';
import { type BreadcrumbItem, useBreadcrumbTitle, useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import type { Delivery } from '@/services/db/delivery/entity';
import { useDelivery, useDeliveryDetailContent } from '@/services/db/delivery/queries';
import { isEmptyString } from '@/utils/string';
import { DeliveryDetailAlert } from './components/delivery-detail-alert';
import { DeliveryDetailMetadata } from './components/delivery-detail-metadata';
import { DeliveryJourneysPanel } from './components/delivery-journeys-panel';
import { DeliveryMessagePreview } from './components/delivery-message-preview';
import { DeliveryMetricsTimeline } from './components/delivery-metrics-timeline';
import { DeliveryPreviewHeader } from './components/delivery-preview-header';
import { InAppConfig } from './components/in-app-config';
import { InboxConfig } from './components/inbox-config';
import { RelatedDeliveries } from './components/related-deliveries';
import { WebhookResponseBlock } from './components/webhook-response-block';

type DeliveryDetailPageProps = {
  /** Delivery id from the route (`/message_activity/sent/:deliveryId`). */
  deliveryId: string;
  /** Builds a related delivery's href — drafts route to Ember, sent to the React detail. */
  getDeliveryHref: (delivery: Delivery) => string;
  /** The list + tab crumbs (from the route view); the subject crumb is appended once loaded. */
  breadcrumbs?: BreadcrumbItem[];
};

/**
 * Delivery detail: the rendered message (preview header, status alert, related
 * deliveries, message body, webhook response) in the main column, and the
 * metadata / channel config / Journeys / metrics-timeline aside beside it, from
 * the existing `services/db` data layer. Still deferred: draft edit mode.
 */
export function DeliveryDetailPage({ deliveryId, getDeliveryHref, breadcrumbs = [] }: DeliveryDetailPageProps) {
  return (
    <Page>
      <PageBody>
        <Suspense fallback={<Spinner />}>
          <DeliveryDetailContent deliveryId={deliveryId} getDeliveryHref={getDeliveryHref} breadcrumbs={breadcrumbs} />
        </Suspense>
      </PageBody>
    </Page>
  );
}

type DeliveryDetailContentProps = {
  deliveryId: string;
  getDeliveryHref: (delivery: Delivery) => string;
  breadcrumbs: BreadcrumbItem[];
};

function DeliveryDetailContent({ deliveryId, getDeliveryHref, breadcrumbs }: DeliveryDetailContentProps) {
  const { data: delivery } = useDelivery({ id: deliveryId });
  const subject = isEmptyString(delivery.subject) ? '(No subject)' : delivery.subject;

  // Ember's `outbox.deliveries.delivery` crumb: the delivery subject, appended to the list/tab
  // crumbs built by the route view. `fs-mask` + truncated — the subject is user-authored content
  // (the same masking the preview header applies). `titleText` keeps the subject out of
  // `document.title`, which analytics and Copilot read and `fs-mask` can't reach; the delivery id
  // still distinguishes one open tab from another, and it is already in the URL they capture.
  useRegisterBreadcrumbs([
    ...breadcrumbs,
    {
      id: 'delivery',
      text: subject,
      titleText: `Delivery ${deliveryId}`,
      href: '#',
      isTruncated: true,
      className: 'fs-mask',
    },
  ]);

  // Ember titles the tab from the same crumb chain, leaf first ("Delivery <id> | Sent | Message
  // activity | Customer.io"). Read back from the breadcrumbs store rather than rebuilt from the
  // prop, so the tab and the rendered trail stay on one source of truth; `DocumentTitle` supplies
  // the trailing "| Customer.io". Empty until the registering effect above has run.
  const documentTitle = useBreadcrumbTitle();

  // Ember's `2fr 1fr` grid: the rendered message on the left, the metadata/metrics aside on the
  // right. `minW="0"` lets the preview column shrink so long subjects/URLs truncate instead of
  // widening the grid. The aside's panels derive purely from the delivery, so they paint
  // immediately while the message body suspends in the main column.
  return (
    <Grid columns="2fr 1fr" gap="200" alignItems="flex-start">
      {isEmptyString(documentTitle) ? null : <DocumentTitle title={documentTitle} />}
      <Box minW="0">
        <Suspense fallback={<Spinner />}>
          <DeliveryPreview deliveryId={deliveryId} delivery={delivery} getDeliveryHref={getDeliveryHref} />
        </Suspense>
      </Box>
      <Stack as="aside" aria-label="Metadata & Metrics" gap="200">
        <DeliveryDetailMetadata delivery={delivery} />
        <DeliveryConfigPanels deliveryId={deliveryId} delivery={delivery} />
        <DeliveryJourneysPanel delivery={delivery} />
        <DeliveryMetricsTimeline delivery={delivery} />
      </Stack>
    </Grid>
  );
}

type DeliveryPreviewProps = {
  deliveryId: string;
  delivery: Delivery;
  getDeliveryHref: (delivery: Delivery) => string;
};

/**
 * The main column: preview header + status alert + related-deliveries
 * (resend-chain) panel + rendered message + webhook response, all reading the
 * suspended detail content. The related-deliveries panel sits between the
 * header and the message (as in Ember); the webhook response follows the
 * message.
 */
function DeliveryPreview({ deliveryId, delivery, getDeliveryHref }: DeliveryPreviewProps) {
  const { data: detail } = useDeliveryDetailContent({ id: deliveryId });

  return (
    <Stack gap="300">
      <DeliveryPreviewHeader delivery={delivery} detail={detail} />
      <DeliveryDetailAlert delivery={delivery} detail={detail} />
      <RelatedDeliveries delivery={delivery} getDeliveryHref={getDeliveryHref} />
      <DeliveryMessagePreview deliveryId={deliveryId} delivery={delivery} />
      <WebhookResponseBlock delivery={delivery} />
    </Stack>
  );
}

type DeliveryConfigPanelsProps = { deliveryId: string; delivery: Delivery };

/**
 * The channel config panels in the aside (in-app / inbox only). Ember renders
 * these beside the metadata. They read the suspended detail content, so they
 * only suspend for the two channels that have config — every other channel
 * renders nothing without gating the aside on a detail fetch.
 */
function DeliveryConfigPanels({ deliveryId, delivery }: DeliveryConfigPanelsProps) {
  const channel = channelValueOf(delivery.action_type);
  if (channel === 'in_app') {
    return (
      <Suspense fallback={<Spinner />}>
        <DeliveryConfigPanelsContent deliveryId={deliveryId} kind="in_app" />
      </Suspense>
    );
  }
  if (channel === 'inbox') {
    return (
      <Suspense fallback={<Spinner />}>
        <DeliveryConfigPanelsContent deliveryId={deliveryId} kind="inbox" />
      </Suspense>
    );
  }
  return null;
}

type DeliveryConfigPanelsContentProps = { deliveryId: string; kind: 'in_app' | 'inbox' };

function DeliveryConfigPanelsContent({ deliveryId, kind }: DeliveryConfigPanelsContentProps) {
  const { data: detail } = useDeliveryDetailContent({ id: deliveryId });
  return kind === 'in_app' ? <InAppConfig detail={detail} /> : <InboxConfig detail={detail} />;
}
